/**
 * Authentication Context
 * Provides current user info from Azure Entra authentication
 * Fetches user from /v2/me endpoint on mount
 */
import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

// ============================================================================
// Types
// ============================================================================

export interface AuthUser {
  id: string;
  email: string;
  clerkId: string | null;
  name: string | null;
  imageUrl: string | null;
}

interface AuthContextValue {
  user: AuthUser | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// ============================================================================
// Context
// ============================================================================

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  error: null,
  refetch: async () => {},
});

// ============================================================================
// API
// ============================================================================

const envVars = import.meta.env as unknown as { VITE_QA_JUDGE_URL?: string };
const isLocal = import.meta.env.DEV;
const API_BASE = isLocal ? envVars.VITE_QA_JUDGE_URL || 'http://localhost:3001' : '';

async function fetchCurrentUser(): Promise<AuthUser | null> {
  try {
    const response = await fetch(`${API_BASE}/v2/me`, {
      credentials: 'include',
    });

    if (!response.ok) {
      if (response.status === 401) {
        // Not authenticated - this is expected in some cases
        return null;
      }
      throw new Error(`Failed to fetch user: ${response.status}`);
    }

    const data = await response.json();
    return data.user;
  } catch (error) {
    console.error('[Auth] Failed to fetch current user:', error);
    return null;
  }
}

// ============================================================================
// Provider
// ============================================================================

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refetch = async () => {
    setLoading(true);
    setError(null);
    try {
      const fetchedUser = await fetchCurrentUser();
      setUser(fetchedUser);
      
      // If not authenticated in deployed environment, redirect to Azure AD login
      // This handles the case where user visits without an Azure session
      if (!fetchedUser && !isLocal) {
        console.log('[Auth] No user session found, redirecting to Azure AD login...');
        const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
        window.location.href = `/.auth/login/aad?post_login_redirect_uri=${returnUrl}`;
        return;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch user');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refetch();
  }, []);

  return (
    <AuthContext.Provider value={{ user, loading, error, refetch }}>
      {children}
    </AuthContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// ============================================================================
// Utilities
// ============================================================================

/**
 * Get initials from user name or email
 */
export function getUserInitials(user: AuthUser | null): string {
  if (!user) return '?';
  
  if (user.name) {
    const parts = user.name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return user.name.substring(0, 2).toUpperCase();
  }
  
  return user.email.substring(0, 2).toUpperCase();
}

/**
 * Get display name for user
 */
export function getUserDisplayName(user: AuthUser | null): string {
  if (!user) return 'Unknown User';
  return user.name || user.email;
}

